import React, { useRef, useEffect, useState, useImperativeHandle, useCallback } from 'react';
import Inputmask from 'inputmask';
import './styles.scss';
import { Label } from '../Label/index';
import _uniqueId from 'lodash/uniqueId';
import { Underline } from '../Underline';
import TsrSvgIcon, { Icon } from '../../assets/icons';
import { InputContainer } from '../InputContainer';
import { InputPropsBase } from '../interfaces/Inputs';
import TsrTooltip from '../../tooltip';
import { TooltipType } from '../models/TooltipType';
import { Variant } from '../models';
import { KEY_ARROW_UP, KEY_ARROW_DOWN, KEY_ESCAPE } from '../models/EventKeysConsts';
import { debounce } from 'lodash';
import { LoadingIcon } from '../../UI';

interface NumericInputProps extends InputPropsBase<number> {
    step?: number;
    digits?: number;
    min?: number;
    max?: number;
    showAdditionalButton?: boolean;
    disabledAdditionalButton?: boolean;
    onAdditionalButtonClick?: () => void;
    additionalButtonIcon?: Icon;
    loading?: boolean;
}

export const NumericInput = React.forwardRef<HTMLInputElement, React.PropsWithChildren<NumericInputProps>>((props, ref) => {
    const id = _uniqueId('id-numeric-input-');
    const timeoutTime = 300;
    const intervalTime = 50;
    const [text, setText] = useState(props.value || 0);
    const innerRef = useRef<HTMLInputElement>(null);
    const [onFocus, setOnFocus] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    useImperativeHandle(ref, () => innerRef.current!);
    const onBlur = () => {
        setOnFocus(false);
        document.removeEventListener('wheel', preventScroll, true);
        if (text < min) {
            !props.immediatelyChange && props.onChange && props.onChange(min);
            props.onBlur && props.onBlur(min);
            setText(min);
        } else if (max && text > max) {
            !props.immediatelyChange && props.onChange && props.onChange(max);
            props.onBlur && props.onBlur(max);
            setText(max);
        } else {
            !props.immediatelyChange && props.onChange && props.onChange(text);
            props.onBlur && props.onBlur(text);
            setText(text);
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setText(parseInt(e.target.value, 10));
    };

    useEffect(() => {
        setText(props.value);
    }, [props.value]);

    const {
        step = props.step || 1,
        digits = props.digits || 0,
        min = props.min || 0,
        max = props.max,
        disabled = props.disabled === 'inputOnly' || props.disabled === true,
    } = props;
    const timeout = useRef<ReturnType<typeof setTimeout>>();
    const interval = useRef<ReturnType<typeof setInterval>>();

    useEffect(() => {
        if (!innerRef.current) {
            return;
        }
        Inputmask({
            alias: 'numeric',
            groupSeparator: '',
            max: max,
            min: min,
            digits: digits,
            digitsOptional: false,
            SetMaxOnOverflow: true,
            step: step,
            prefix: '',
            placeholder: '0',
            nullable: false,
            positionCaretOnTab: false,
            shortcuts: null,
            enforceDigitsOnBlur: true,
            rightAlign: false,
            oncleared() {
                if (innerRef.current?.selectionStart === text && innerRef.current?.selectionEnd === text) {
                    setText(0);
                    innerRef.current?.setSelectionRange(1, 1);
                }
            },
        }).mask(innerRef.current);
    }, [digits, max, min, step]);

    const debouncedOnChange = useCallback(
        debounce((value: number) => props.onChange && props.onChange(value), 275),
        [],
    );

    const incrementValueHandler = (change: number): void => {
        if (!innerRef.current) {
            return;
        }
        const selectionStart = innerRef.current.selectionStart,
            selectionEnd = innerRef.current.selectionEnd,
            selectionEndInput = selectionEnd === innerRef.current.value.length,
            selectionEndInteger = selectionEnd === innerRef.current.value.length - digits - 1;

        let selectionStartNew = innerRef.current.selectionStart;
        let updatedSelectionStart = 0;
        selectionStartNew == null ? (selectionStartNew = updatedSelectionStart) : (updatedSelectionStart = selectionStartNew);
        const carretPosition = selectionStartNew - innerRef.current.value.length;
        let updatedCarretPosition = carretPosition < 0 - digits ? step : step / Math.pow(10, digits);
        if (change < 0) {
            updatedCarretPosition = updatedCarretPosition * -1;
        }
        const newValue = +innerRef.current.value + updatedCarretPosition;
        innerRef.current.value = String(newValue);

        innerRef.current.selectionEnd = selectionEndInput
            ? innerRef.current.value.length
            : selectionEndInteger
            ? innerRef.current.value.length - digits - 1
            : selectionEnd;
        innerRef.current.selectionStart = selectionStart === selectionEnd ? innerRef.current.selectionEnd : selectionStart;
        setText(+innerRef.current.value);
        if (props.variant === Variant.AgGrid || props.immediatelyChange) {
            if (props.disableDebounce) {
                props.onChange && props.onChange(+innerRef.current.value);
            } else {
                debouncedOnChange(+innerRef.current.value);
            }
        }
    };

    const onMouseDownHandler = useCallback((change: number, e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        e.preventDefault();
        innerRef.current?.focus();
        incrementValueHandler(change);
        setIsMouseDown(true);
        timeout.current = setTimeout(() => {
            interval.current = setInterval(() => {
                incrementValueHandler(change);
                setIsMouseDown(true);
            }, intervalTime);
        }, timeoutTime);
    }, []);

    const clearTimeOuts = useCallback((): void => {
        if (interval.current) {
            clearTimeout(interval.current);
        }
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        if (isMouseDown) {
            if (props.variant === Variant.AgGrid || props.immediatelyChange) {
                if (props.disableDebounce && !props.immediatelyChange) {
                    props.onChange && props.onChange(text);
                } else {
                    debouncedOnChange(text);
                }
            }
            document.removeEventListener('mouseup', clearTimeOuts);
            setIsMouseDown(false);
        }
    }, [text]);

    const onMouseUp = useCallback((): void => {
        if (interval.current) {
            clearTimeout(interval.current);
        }
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        setIsMouseDown(false);
    }, [text]);

    useEffect(() => {
        if (isMouseDown) {
            document.addEventListener('mouseup', clearTimeOuts);
        }
    }, [isMouseDown, text]);

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === KEY_ARROW_UP) {
            event.preventDefault();
            incrementValueHandler(step);
        }
        if (event.key === KEY_ARROW_DOWN) {
            event.preventDefault();
            incrementValueHandler(-step);
        }
        if (event.key === KEY_ESCAPE) {
            event.currentTarget.blur();
            onBlur();
        }
    };

    const onKeyUpHandler = (e: any): void => {
        (props.variant === Variant.AgGrid || props.immediatelyChange) && props.onChange?.(text);
    };

    const preventScroll = useCallback((e: globalThis.WheelEvent) => {
        e.stopPropagation();
        e.preventDefault();
        e.deltaY < 0 ? incrementValueHandler(step) : incrementValueHandler(-step);
        return false;
    }, []);

    useEffect(() => {
        if (onFocus) {
            document.addEventListener('wheel', preventScroll, { passive: false, capture: true });
        } else {
            document.removeEventListener('wheel', preventScroll, true);
        }

        return () => document.removeEventListener('wheel', preventScroll, true);
    }, [onFocus, preventScroll]);

    return (
        <InputContainer
            gap={props.gap}
            justify={props.justify}
            align={props.align}
            overflow={props.overflow}
            width={props.width || '100%'}
            className={`${props.className} ${props.variant}`}
            inputContainerLength={props.inputLength}
        >
            <Label
                id={props.id || id}
                content={props.label}
                disabled={props.disabled}
                labelPosition={props.labelPosition}
                labelLength={props.labelLength || 1}
                className={props.labelClassName}
            >
                <div
                    className={'tsr-input-numeric' + (props.disabled === true ? ' disabled' : '')}
                    style={{ flex: props.inputLength || 1, width: props.inputFieldWidth }}
                >
                    <input
                        type="text"
                        id={id}
                        className={'input-type' + (props.disabled === true ? ' disabled' : '')}
                        ref={innerRef}
                        value={text}
                        tabIndex={props.tabIndex}
                        onKeyDown={onKeyDownHandler}
                        onKeyUp={onKeyUpHandler}
                        onChange={onChange}
                        onBlur={onBlur}
                        onFocus={() => setOnFocus(true)}
                        disabled={props.disabled === 'inputOnly' || props.disabled === true}
                    ></input>
                    {props.errorMessage && !props.disabled ? (
                        <TsrTooltip type={TooltipType.Error} icon={true} content={props.errorMessage}>
                            <TsrSvgIcon icon="information" color="error" pointer />
                        </TsrTooltip>
                    ) : props.infoMessage && !props.disabled ? (
                        <TsrTooltip icon={true} content={props.infoMessage}>
                            <TsrSvgIcon icon="information" color="info" pointer />
                        </TsrTooltip>
                    ) : (
                        props.infoMessage && <TsrSvgIcon icon="information" color="disabled" />
                    )}
                    {props.showAdditionalButton && (
                        <button
                            type="button"
                            className={'button' + (props.disabledAdditionalButton ? ' disabled' : '')}
                            tabIndex={-1}
                            disabled={disabled === 'inputOnly' || props.disabledAdditionalButton}
                            onClick={() => props.onAdditionalButtonClick && props.onAdditionalButtonClick()}
                        >
                            {props.loading ? (
                                <LoadingIcon loading={props.loading} color="primary" />
                            ) : (
                                <TsrSvgIcon icon={props.additionalButtonIcon || Icon.Refresh} color="info" pointer />
                            )}
                        </button>
                    )}
                    <div className="buttons">
                        <button
                            type="button"
                            className={'mouse' + (props.disabled === true ? ' disabled' : '')}
                            tabIndex={-1}
                            disabled={disabled === 'inputOnly' || disabled === true}
                            onMouseDown={e => onMouseDownHandler(step, e)}
                            onMouseUp={onMouseUp}
                        >
                            <TsrSvgIcon icon="arrowUp" color="info" pointer />
                        </button>
                        <button
                            type="button"
                            className={'mouse' + (props.disabled === true ? ' disabled' : '')}
                            tabIndex={-1}
                            disabled={disabled === 'inputOnly' || disabled === true}
                            onMouseDown={e => onMouseDownHandler(-step, e)}
                            onMouseUp={onMouseUp}
                        >
                            <TsrSvgIcon icon="arrowDown" color="info" pointer />
                        </button>
                    </div>

                    <Underline disabled={props.disabled === 'inputOnly' || props.disabled === true} />
                </div>
            </Label>
        </InputContainer>
    );
});
